import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { interval, Observable, Subject, Subscriber, Subscription, TeardownLogic, throwError } from 'rxjs';
import { catchError, switchMap, takeUntil, timeout } from 'rxjs/operators';

import { RestBuilder } from '../../services/helpers/rest.builder';
import {
  IChatMessageStatus,
  ISendChatMessageRequest,
  ISendChatMessageResponse,
} from '../../../../shared/interfaces/ai-chat.interface';
import { ApiHeaderOption } from '../../../../shared/constants/api';

export const THREE_MINUTES_MS: number = 180000; // 3 minutes default
export const TIMEOUT_DURATION: HttpContextToken<number> = new HttpContextToken<number>((): number => THREE_MINUTES_MS);

export interface IChatMessagesState {
  messages: ISendChatMessageResponse[];
  pendingUserMessage: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AiChatService {
  private readonly restBuilder: RestBuilder;
  private stopPolling$: Subject<void> = new Subject<void>();

  constructor(private httpClient: HttpClient) {
    this.restBuilder = new RestBuilder();
  }

  private chat(id?: string): RestBuilder {
    return this.restBuilder.create().ai().chat(id);
  }

  public sendMessage(
    sendChatMessageRequest: ISendChatMessageRequest,
    timeoutMs: number = THREE_MINUTES_MS,
  ): Observable<ISendChatMessageResponse> {
    const context: HttpContext = new HttpContext().set(TIMEOUT_DURATION, timeoutMs);

    return this.httpClient
      .post<ISendChatMessageResponse>(this.chat().getApiUrl(), sendChatMessageRequest, {
        headers: { [ApiHeaderOption.skipLoader]: 'true' },
        context: context,
      })
      .pipe(
        timeout(timeoutMs),
        catchError((error: any) => {
          if (error.name === 'TimeoutError') {
            return throwError({
              status: 504, // Gateway Timeout
              message: 'ai_request_timed_out',
            });
          }
          return throwError(error);
        }),
      );
  }

  public setRating(id: string, rating: number): Observable<ISendChatMessageResponse> {
    const url: string = this.chat(id).rating().getApiUrl();

    return this.httpClient.put<ISendChatMessageResponse>(url, { rating });
  }

  public unsetRating(id: string): Observable<ISendChatMessageResponse> {
    const url: string = this.chat(id).rating().getApiUrl();

    return this.httpClient.delete<ISendChatMessageResponse>(url);
  }

  public getMessagesBySessionId(sessionId: string): Observable<ISendChatMessageResponse[]> {
    const url: string = this.chat().session(sessionId).getApiUrl();

    return this.httpClient.get<ISendChatMessageResponse[]>(url);
  }

  public getMessagesWithPolling(sessionId: string): Observable<IChatMessagesState> {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.stopPolling$ = new Subject<void>();

    return this.getMessagesBySessionId(sessionId).pipe(
      switchMap((initialResult: ISendChatMessageResponse[]): Observable<IChatMessagesState> => {
        const sorted: ISendChatMessageResponse[] = initialResult.sort(
          (a: ISendChatMessageResponse, b: ISendChatMessageResponse): number =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        const responsePending: boolean =
          sorted.length > 0 &&
          (sorted[0].status === IChatMessageStatus.pending || sorted[0].status === IChatMessageStatus.processing);

        const initialState: IChatMessagesState = responsePending
          ? { messages: sorted.slice(1), pendingUserMessage: sorted[0].message }
          : { messages: sorted, pendingUserMessage: null };

        if (!responsePending) {
          return new Observable<IChatMessagesState>((stateEmitter: Subscriber<IChatMessagesState>): void => {
            stateEmitter.next(initialState);
            stateEmitter.complete();
          });
        }

        return new Observable<IChatMessagesState>((stateEmitter: Subscriber<IChatMessagesState>): TeardownLogic => {
          stateEmitter.next(initialState);

          const subscription: Subscription = interval(1000)
            .pipe(
              takeUntil(this.stopPolling$),
              switchMap((): Observable<ISendChatMessageResponse[]> => this.getMessagesBySessionId(sessionId)),
              switchMap((result: ISendChatMessageResponse[]): Observable<IChatMessagesState> => {
                const sortedResult: ISendChatMessageResponse[] = result.sort(
                  (a: ISendChatMessageResponse, b: ISendChatMessageResponse): number =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                );

                const matchingResponse: string =
                  sortedResult.length > 0 &&
                  sortedResult[0].message === initialState.pendingUserMessage &&
                  sortedResult[0].response;

                if (matchingResponse) {
                  this.stopPolling$.next();
                  this.stopPolling$.complete();
                  return new Observable<IChatMessagesState>((subscriber: Subscriber<IChatMessagesState>): void => {
                    subscriber.next({ messages: sortedResult, pendingUserMessage: null });
                    subscriber.complete();
                  });
                }

                return new Observable<IChatMessagesState>((subscriber: Subscriber<IChatMessagesState>): void => {
                  subscriber.next({ messages: sorted.slice(1), pendingUserMessage: sorted[0].message });
                });
              }),
            )
            .subscribe({
              next: (state: IChatMessagesState): void => stateEmitter.next(state),
              error: (error: any): void => stateEmitter.error(error),
              complete: (): void => stateEmitter.complete(),
            });

          return (): void => subscription.unsubscribe();
        });
      }),
    );
  }

  public stopPolling(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }
}
